import {
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  useCardSurface,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { useId, useRef, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { useHostStatusAction } from '../internals/hostStatusAction';
import {
  formatFullDateTime,
  formatMessageTime,
  formatRelativeTime,
} from '../internals/localTime';
import {
  statusLineOverlay,
  statusLineReserve,
  type StatusLineOverlay,
} from '../internals/statusLineOverlay';
import { useFitsLine } from '../internals/useFitsLine';
import { useMinuteTick } from '../internals/useMinuteTick';
import { useStillRunning } from '../internals/useStillRunning';
import { useTimeFormat } from '../internals/useTimeFormat';
import { ICON_SM_CLASS, sizingClass, TEXT_STYLE, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

type LineTone = 'default' | 'done' | 'danger' | 'muted' | 'warn';
type LineIcon = 'none' | 'check' | 'spinner' | 'shield';

const LINE_TONE: Record<LineTone, string> = {
  default: TONE_CLASS.subtle,
  done: TONE_CLASS.done,
  danger: TONE_CLASS.danger,
  muted: TONE_CLASS.muted,
  warn: TONE_CLASS.warn,
};

const ACTION_CLASS =
  'shrink-0 rounded-sm font-medium text-current underline-offset-2 outline-none hover:underline focus-visible:underline focus-visible:ring-1 focus-visible:ring-border-strongest';

// The spaces keep the line's text readable as one sentence; flex drops them visually
const Dot = () => <span aria-hidden="true"> · </span>;

function LineIconGlyph({ icon }: { icon: LineIcon }) {
  if (icon === 'shield')
    return <Icons.shield className="size-[calc(13*var(--ac-u))] shrink-0" />;
  if (icon === 'check')
    return <Icons.check className={cn(ICON_SM_CLASS, 'shrink-0')} />;
  if (icon === 'spinner') {
    return (
      <Icons.loader
        className={cn(
          ICON_SM_CLASS,
          'shrink-0 animate-spin motion-reduce:animate-none'
        )}
      />
    );
  }
  return null;
}

function ActionLink({
  label,
  onPress,
  afterContent,
  autoFocus,
}: {
  label: string;
  onPress: () => void;
  afterContent: boolean;
  autoFocus?: boolean;
}) {
  return (
    <>
      {afterContent ? <Dot /> : null}
      <button
        type="button"
        className={ACTION_CLASS}
        autoFocus={autoFocus}
        onClick={onPress}
      >
        {label}
      </button>
    </>
  );
}

function Line({
  tone,
  sizing,
  children,
  reserveText,
  detail,
  row,
}: {
  reserveText: string[];
  tone: LineTone;
  sizing: 'hug' | 'fill' | undefined;
  children: ReactNode;
  detail: string | undefined;
  row: boolean;
}) {
  const detailId = useId();
  return (
    <p
      role="status"
      title={detail}
      aria-describedby={detail ? detailId : undefined}
      className={cn(
        // The slot keeps one line: copy that doesn't fit is shortened at its source
        'relative grid min-h-[var(--ac-meta-lh)] min-w-0 items-center whitespace-nowrap',
        // A row's time or urgency is an atom beside the title, never squeezed
        row && 'shrink-0',
        TEXT_STYLE.meta,
        LINE_TONE[tone],
        sizingClass(sizing)
      )}
    >
      {detail ? (
        <span id={detailId} className="sr-only">
          {detail}
        </span>
      ) : null}
      {[...new Set(reserveText)].map((text) => (
        <span
          key={text}
          aria-hidden="true"
          data-reserve={text}
          className="invisible col-start-1 row-start-1 before:content-[attr(data-reserve)]"
        />
      ))}
      <span
        className={cn(
          'flex min-w-0 items-center gap-x-[var(--ac-gap-xs)]',
          reserveText && 'absolute inset-0'
        )}
      >
        {children}
      </span>
    </p>
  );
}

function OverlayContent({ overlay }: { overlay: StatusLineOverlay }) {
  const measureRef = useRef<HTMLSpanElement>(null);
  const fits = useFitsLine(measureRef, overlay.text);
  const text =
    overlay.countText !== undefined && !fits ? overlay.countText : overlay.text;
  return (
    <>
      {overlay.countText !== undefined ? (
        <span
          ref={measureRef}
          aria-hidden="true"
          data-measure={overlay.text}
          className="invisible pointer-events-none absolute left-0 top-0 before:content-[attr(data-measure)]"
        />
      ) : null}
      <span className="min-w-0">{text}</span>
      {overlay.action ? (
        <ActionLink
          label={overlay.action.label}
          onPress={overlay.action.run}
          afterContent
        />
      ) : null}
    </>
  );
}

// The card's one status line. The session's local states take its place in order
// of urgency; otherwise it shows the document's text, time and action
export const StatusLine: ComponentFn<typeof catalog, 'StatusLine'> = ({
  props,
  emit,
}) => {
  const session = useCardSession();
  const controls = useCardSessionControls();
  const status = useCardStatus();
  const row = useCardSurface().endsWith('_row');
  const hostAction = useHostStatusAction();
  const relative = props.atFormat === 'relative';
  useMinuteTick(relative && props.at !== undefined);
  const prefer12Hr = useTimeFormat();

  const { pendingVerb } = props;
  const stillRunning = useStillRunning(
    status === 'running' &&
      pendingVerb !== undefined &&
      !props.quietWhileRunning
  );
  const overlay = statusLineOverlay(
    session,
    controls,
    { pendingVerb, fixVerb: props.fixVerb ?? 'send' },
    stillRunning,
    row
  );
  const time = props.at
    ? relative
      ? formatRelativeTime(props.at)
      : formatMessageTime(props.at, prefer12Hr)
    : undefined;
  const lineReserve = statusLineReserve(
    { ...props, time, timeJoin: relative ? ' ' : ' · ' },
    status,
    row,
    session.hasChecks,
    session.validationCount > 0
  );
  // A row's line hugs its reserve, so the host's action must be part of it
  const reserveText =
    row && hostAction
      ? lineReserve.map((text) => `${text} · ${hostAction.label}`)
      : lineReserve;
  if (overlay) {
    return (
      <Line
        tone={overlay.tone}
        sizing={props.sizing}
        reserveText={reserveText}
        detail={undefined}
        row={row}
      >
        <OverlayContent overlay={overlay} />
      </Line>
    );
  }

  const { text, at, actionLabel } = props;
  // "Edited" says the draft differs from what Littlebird prepared, so it stays only
  // while the card is still a draft
  // and only when the slot has no other text: a caveat takes the one line
  const showsEdited = session.edited && status === 'draft' && !text;

  return (
    <Line
      tone={props.tone ?? 'default'}
      sizing={props.sizing}
      reserveText={reserveText}
      detail={props.detail}
      row={row}
    >
      <LineIconGlyph icon={props.icon ?? 'none'} />
      {text ? <span className="min-w-0 truncate">{text}</span> : null}
      {at ? (
        <>
          {text ? relative ? ' ' : <Dot /> : null}
          <time
            dateTime={at}
            title={formatFullDateTime(at, prefer12Hr)}
            className="shrink-0 whitespace-nowrap"
          >
            {time}
          </time>
        </>
      ) : null}
      {actionLabel ? (
        <ActionLink
          label={actionLabel}
          onPress={() => emit('press')}
          afterContent={Boolean(text || at)}
        />
      ) : null}
      {hostAction ? (
        // ml-auto pushes the fold-back control to the line's right end
        <span
          data-host-status-action
          className="ml-auto flex shrink-0 items-center"
        >
          <ActionLink
            label={hostAction.label}
            onPress={hostAction.run}
            autoFocus={hostAction.focus}
            afterContent={Boolean(text || at || actionLabel)}
          />
        </span>
      ) : null}
      {showsEdited ? (
        <>
          <span className={cn('shrink-0', TONE_CLASS.muted)}>Edited</span>
        </>
      ) : null}
    </Line>
  );
};
